import { z } from 'zod';

/**
 * WHO AN ANNOUNCEMENT IS FOR — a closed vocabulary, not a free jsonb bag.
 *
 * `platform.announcement.audience_rule` shipped as an unconstrained `jsonb`
 * holding a SENTENCE — `{"description": "Depositors last 30d"}` — and the
 * console, the API and the docs all said the same true thing about it: nothing
 * could turn that sentence into a set of people, so nothing could send an
 * announcement. The missing piece was never the mailer.
 *
 * This file is the vocabulary that closes it, and it is deliberately SMALL.
 * Every rule below is one an operator can justify to a regulator and — the
 * property that matters more — one the platform can COUNT BEFORE THE SEND. A
 * rule that cannot be counted is a rule whose blast radius is discovered
 * afterwards, which is how one mistake becomes two thousand mistakes.
 *
 * THE SAME SHAPE THE REPORTS BUILDER USES, AND FOR THE SAME REASON. A request
 * names a KEY; the key selects a fixed SQL predicate written in
 * `services/api/src/modules/platform-admin/audience.ts`; a key that is not in
 * that map is REFUSED rather than defaulted. Nothing a caller types becomes
 * SQL, and — the failure mode that matters here rather than injection — an
 * unrecognised rule can never fall through to "everybody".
 *
 * ── WHAT IS DELIBERATELY NOT HERE ─────────────────────────────────────────
 *
 * "Depositors last 30d", "VIP 3 and above" and "Dormant 30+ days" were three of
 * the five phrases the old free-text field suggested. None of them is here.
 * Each is countable in principle and none of them is countable in a way this
 * stream verified end to end, and a rule that is offered is a rule an operator
 * will send against. The vocabulary grows by adding a key here and a predicate
 * beside it, both at once, or it does not grow.
 */

/** The rules the resolver understands. Anything else is refused. */
export const ANNOUNCEMENT_AUDIENCE_RULES = [
  'ALL_PLAYERS',
  'KYC_STATUS',
  'ACTIVE_BONUS',
  'SELF_EXCLUDED',
  'NAMED_PLAYERS',
] as const;

export type AnnouncementAudienceRuleKey = (typeof ANNOUNCEMENT_AUDIENCE_RULES)[number];

/**
 * `player.player.kyc_status`, as the column actually holds it.
 *
 * A closed set rather than a string, because this value reaches a parameterised
 * comparison and a typo would silently resolve to nobody — an audience of zero
 * that reads on screen as a successful send to a carefully chosen segment.
 */
export const announcementKycStatus = z.enum(['UNVERIFIED', 'PENDING', 'VERIFIED', 'REJECTED']);
export type AnnouncementKycStatus = z.infer<typeof announcementKycStatus>;

/**
 * The rule itself.
 *
 * A discriminated union rather than `{ key, params }`, so a rule that takes a
 * parameter cannot be expressed without it and a rule that takes none cannot
 * carry one. `KYC_STATUS` with no status is not a narrower audience — it is an
 * unanswerable question, and the shape refuses it before the resolver has to.
 */
export const announcementAudienceRule = z.discriminatedUnion('rule', [
  z.object({ rule: z.literal('ALL_PLAYERS') }),
  z.object({ rule: z.literal('KYC_STATUS'), kycStatus: announcementKycStatus }),
  z.object({ rule: z.literal('ACTIVE_BONUS') }),
  z.object({ rule: z.literal('SELF_EXCLUDED') }),
  /**
   * A list the operator chose, by player id.
   *
   * ── WHY THIS IS A RULE AT ALL, WHEN IT IS PLAINLY A SNAPSHOT ─────────────
   *
   * Every other rule here describes a PROPERTY and is re-evaluated at send
   * time, because the set can move between composing and sending. A list of
   * ids cannot move, which makes it the one audience whose count is the same
   * number twice — and that is a feature rather than a cheat: "these fourteen
   * players were affected by Tuesday's outage" is a real thing an operator
   * needs to say, and there is no property those fourteen share.
   *
   * It still goes through the same resolver, so it inherits the brand scope,
   * the address check, the suppression list and the delivery record. A separate
   * "message these players" path would have had none of that.
   *
   * ── AND WHY IT CAN NEVER CARRY A PROMOTION ───────────────────────────────
   *
   * `promotionalAllowed: false`, and it is the more important half of the
   * design. A hand-picked promotional list IS a targeted marketing send, and
   * this platform addresses marketing by recorded consent rather than by
   * selection — that is what `player.may_be_marketed_to` exists to enforce.
   * Allowing a promotion here would let an operator route round it by choosing
   * the people one at a time.
   *
   * The cap is 500, matching the roster's bulk actions: an audience an operator
   * cannot picture is one whose blast radius is discovered afterwards.
   */
  z.object({
    rule: z.literal('NAMED_PLAYERS'),
    playerIds: z
      .array(z.string().min(1))
      .min(1, 'Choose at least one player.')
      .max(500, 'Address at most 500 players in one announcement.'),
  }),
]);
export type AnnouncementAudienceRule = z.infer<typeof announcementAudienceRule>;

/**
 * What each rule is called on screen, and what it is FOR.
 *
 * `promotionalAllowed` is the compliance half and it is stated here — beside
 * the vocabulary — rather than only in the route, because the console has to
 * grey the option out with its true reason BEFORE an operator composes against
 * it. See `SELF_EXCLUDED` below.
 */
export interface AnnouncementAudienceRuleInfo {
  key: AnnouncementAudienceRuleKey;
  label: string;
  /** Shown under the option. States what the platform counts, in one sentence. */
  hint: string;
  /**
   * May a PROMOTIONAL announcement be addressed to this audience at all?
   *
   * FALSE for exactly one rule, and it is not a preference. Self-exclusion is a
   * person asking the operator to stop tempting them; a marketing message is
   * precisely the temptation. Addressing a promotion AT that group is not a
   * mistake to be filtered out afterwards — it is the one send a licensed
   * operator must never compose.
   */
  promotionalAllowed: boolean;
}

export const ANNOUNCEMENT_AUDIENCE_RULE_INFO: Record<
  AnnouncementAudienceRuleKey,
  AnnouncementAudienceRuleInfo
> = {
  ALL_PLAYERS: {
    key: 'ALL_PLAYERS',
    label: 'Every player on the brand',
    hint: 'Every account in the announcement’s brand scope. The widest audience this platform can address.',
    promotionalAllowed: true,
  },
  KYC_STATUS: {
    key: 'KYC_STATUS',
    label: 'Players at one KYC status',
    hint: 'Accounts whose verification currently stands at the status you pick — the column the KYC queue moves.',
    promotionalAllowed: true,
  },
  ACTIVE_BONUS: {
    key: 'ACTIVE_BONUS',
    label: 'Players with a live bonus',
    hint: 'Accounts holding a bonus grant that is still ACTIVE and has not expired.',
    promotionalAllowed: true,
  },
  SELF_EXCLUDED: {
    key: 'SELF_EXCLUDED',
    label: 'Players who have self-excluded',
    hint: 'Accounts under a self-exclusion that is in force. Operational notices only — a promotion may never be addressed to this group.',
    promotionalAllowed: false,
  },
  NAMED_PLAYERS: {
    key: 'NAMED_PLAYERS',
    label: 'A list of players you choose',
    hint: 'The accounts you selected, by reference. Operational notices only — a hand-picked promotional list is a targeted marketing send, and this platform addresses marketing by consent rather than by selection.',
    promotionalAllowed: false,
  },
};

/**
 * The one-line description stored beside the rule.
 *
 * DERIVED, never typed. `audience_rule->>'description'` is read by the list
 * screen and by two e2e assertions, and it used to be whatever an operator
 * wrote — so two people describing the same audience wrote two different
 * sentences, and neither sentence was checked against what would actually be
 * sent to. Generating it from the rule means the words on screen and the set of
 * people are the same fact.
 */
export function announcementAudienceDescription(rule: AnnouncementAudienceRule): string {
  switch (rule.rule) {
    case 'ALL_PLAYERS':
      return 'Every player on the brand';
    case 'KYC_STATUS':
      return `Players at KYC ${rule.kycStatus.toLowerCase()}`;
    case 'ACTIVE_BONUS':
      return 'Players with a live bonus';
    case 'SELF_EXCLUDED':
      return 'Players who have self-excluded';
    case 'NAMED_PLAYERS':
      /* The COUNT, not the ids. This string is stored on the announcement and
         read back by the list screen, and a description carrying five hundred
         identifiers is not a description — it is the audience again, in a
         column nobody can scan. The ids live in the rule beside it. */
      return `${rule.playerIds.length} player${rule.playerIds.length === 1 ? '' : 's'} you chose`;
  }
}

/**
 * Asking how big an audience is, before anything is created.
 *
 * The composer calls this while the operator is still typing, which is the only
 * moment the number is worth anything: a size discovered after the send is a
 * post-mortem. `kind` is required because the answer DEPENDS on it — the same
 * rule resolves to two different sets for a mandatory notice and a promotion,
 * and a preview that ignored the kind would show the operator a number that is
 * not the number that would be sent to.
 */
export const previewAnnouncementAudienceRequest = z.object({
  kind: z.enum(['MANDATORY', 'PROMOTIONAL']),
  audience: announcementAudienceRule,
  /** Absent means platform-wide — every brand, and only an unscoped admin may. */
  brandId: z.string().min(1).optional(),
});
export type PreviewAnnouncementAudienceRequest = z.infer<
  typeof previewAnnouncementAudienceRequest
>;

/**
 * SENDING, and why the count is in the request.
 *
 * `expectedRecipients` is the number the operator was shown when they pressed
 * the button. The route resolves the audience again inside its own transaction
 * and refuses if the two disagree.
 *
 * That is not ceremony. Between the preview and the press, a player can
 * self-exclude, a bonus can expire, and a hundred accounts can be created by
 * the registration route — and the operator would then approve a send whose
 * size they were never shown. A send is irreversible in the only way that
 * matters (the people who received it have it), so the confirmation is on the
 * SIZE and not merely on the intent.
 */
export const sendAnnouncementRequest = z.object({
  expectedRecipients: z.number().int().nonnegative(),
});
export type SendAnnouncementRequest = z.infer<typeof sendAnnouncementRequest>;
